import { useEffect, useState } from 'react';
import Modal from '@/components/ui/Modal';
import Botao from '@/components/ui/Botao';
import { supabase } from '@/lib/supabase';
import { mensagemAmigavel } from '@/lib/mensagemAmigavel';
import type { SituacaoPedidoCaixa } from '../types';

interface Props {
  aberto: boolean;
  pedidoId: number | null;
  /** Direção pode estornar venda feita na maquininha. */
  direcao: boolean;
  aoFechar: () => void;
  aoDesfeito: (mensagem: string) => void;
}

// Desfazer venda: dinheiro → `caixa_desfazer_venda`; maquininha (com cobrança) →
// só a direção estorna via `caixa-cobrar estornar`. Descobre o caminho pelo
// `cobranca` do `caixa_situacao_pedido`.
export default function ModalDesfazer({
  aberto,
  pedidoId,
  direcao,
  aoFechar,
  aoDesfeito,
}: Props) {
  const [temCobranca, setTemCobranca] = useState<boolean | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [motivo, setMotivo] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  useEffect(() => {
    if (!aberto || pedidoId == null) return;
    let ativo = true;
    setCarregando(true);
    setErro(null);
    setMotivo('');
    setTemCobranca(null);
    (async () => {
      try {
        const { data, error } = await supabase.rpc('caixa_situacao_pedido', { p_pedido: pedidoId });
        if (error) throw error;
        if (!ativo) return;
        setTemCobranca(!!(data as SituacaoPedidoCaixa)?.cobranca);
      } catch (e) {
        if (ativo) setErro(mensagemAmigavel(e));
      } finally {
        if (ativo) setCarregando(false);
      }
    })();
    return () => {
      ativo = false;
    };
  }, [aberto, pedidoId]);

  const bloqueadoDirecao = temCobranca === true && !direcao;

  async function confirmar() {
    if (pedidoId == null || !motivo.trim()) return;
    setEnviando(true);
    setErro(null);
    try {
      if (temCobranca) {
        const { data, error } = await supabase.functions.invoke('caixa-cobrar', {
          body: { acao: 'estornar', pedido_id: pedidoId, motivo: motivo.trim() },
        });
        if (error) {
          setErro('Sem conexão agora. Tente de novo.');
          return;
        }
        if (!data?.ok) {
          setErro(data?.erro ?? 'Não foi possível estornar. Tente de novo.');
          return;
        }
        aoDesfeito(data?.mensagem || 'Venda estornada no cartão.');
      } else {
        const { data, error } = await supabase.rpc('caixa_desfazer_venda', {
          p_pedido: pedidoId,
          p_motivo: motivo.trim(),
        });
        if (error) throw error;
        aoDesfeito(typeof data === 'string' && data ? data : 'Venda desfeita.');
      }
      aoFechar();
    } catch (e) {
      setErro(mensagemAmigavel(e));
    } finally {
      setEnviando(false);
    }
  }

  return (
    <Modal aberto={aberto} titulo="Desfazer venda" aoFechar={aoFechar}>
      {carregando ? (
        <div className="h-16 animate-pulse rounded-xl bg-secondary-100" />
      ) : bloqueadoDirecao ? (
        <div className="rounded-2xl bg-warning-50 px-4 py-3 text-sm font-medium text-warning-800">
          Venda na maquininha: peça à direção para desfazer.
        </div>
      ) : (
        <>
          <p className="text-sm text-secondary-500">
            {temCobranca
              ? 'O valor volta para o cartão do cliente e as fichas deixam de valer. Diga por quê.'
              : 'O estoque volta e a venda sai do total. Diga por quê.'}
          </p>
          {erro && <p className="mt-2 text-sm text-danger-600">{erro}</p>}
          <textarea
            value={motivo}
            onChange={(e) => setMotivo(e.target.value)}
            placeholder="Motivo"
            maxLength={200}
            className="mt-3 w-full rounded-xl border-2 border-secondary-300 bg-white px-3 py-2 text-base text-foreground-950 outline-none focus:border-primary-500"
          />
        </>
      )}

      <div className="mt-5 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
        <Botao variante="secundario" onClick={aoFechar}>
          {bloqueadoDirecao ? 'Entendi' : 'Cancelar'}
        </Botao>
        {!bloqueadoDirecao && !carregando && (
          <Botao
            variante="perigo"
            carregando={enviando}
            disabled={!motivo.trim()}
            onClick={confirmar}
          >
            Desfazer
          </Botao>
        )}
      </div>
    </Modal>
  );
}